import React, { useEffect } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Grid,
  InputAdornment,
  SxProps,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  useFormContext,
} from "react-hook-form";
import CustomTooltip from "./customTooltip";

export interface SelectImageType {
  id?: number | string;
  code?: string;
  label: string;
  phone?: string;
  icon?: string;
  image?: string;
  [key: string]: any;
}

export type AutoCompleteSelectWithImageProps = Omit<
  TextFieldProps,
  "name" | "type" | "onChange"
> & {
  validation?: ControllerProps["rules"];
  name?: string;
  toolTipTitle?: string;
  options?: SelectImageType[];
  valueKey?: string;
  labelKey?: string;
  iconKey?: string;
  type?: "string" | "number";
  sxProps?: SxProps;
  objectOnChange?: boolean;
  onChange?: (value: any) => void;
  control?: Control<any>;
  fieldName?: string;
  label?: string;
  placeholder?: string;
  value?: SelectImageType | null;
  countries?: SelectImageType[];
  width?: string | number;
};

type ControlledAutocompleteFieldProps = {
  fieldValue: any;
  fieldOnChange: (...event: any[]) => void;
  onBlur: () => void;
  fieldError?: { message?: string };
  selectOptions: SelectImageType[];
  valueKey: string;
  labelKey: string;
  iconKey: string;
  fieldName: string;
  defaultAutocompleteSx: SxProps;
  sxProps?: SxProps;
  disabled?: boolean;
  onChange?: (value: any) => void;
  getOptionIcon: (option?: SelectImageType) => string;
  renderInput: (
    params: any,
    selectedValue: SelectImageType | null,
    inputErrorMessage?: string,
    hasError?: boolean,
  ) => React.ReactNode;
};

const ControlledAutocompleteField: React.FC<ControlledAutocompleteFieldProps> = ({
  fieldValue,
  fieldOnChange,
  onBlur,
  fieldError,
  selectOptions,
  valueKey,
  labelKey,
  iconKey,
  fieldName,
  defaultAutocompleteSx,
  sxProps,
  disabled,
  onChange,
  getOptionIcon,
  renderInput,
}) => {
  const { setValue } = useFormContext();

  const selectedOption =
    fieldValue && typeof fieldValue === "object"
      ? fieldValue
      : selectOptions.find((option) => option[valueKey] === fieldValue) || null;

  useEffect(() => {
    if (selectedOption) {
      setValue(fieldName, selectedOption[valueKey]);
    }
  }, [fieldName, selectedOption, setValue, valueKey]);

  return (
    <Autocomplete
      options={selectOptions}
      size="small"
      autoHighlight
      fullWidth
      disabled={disabled}
      value={selectedOption}
      defaultValue={null}
      sx={{ ...(defaultAutocompleteSx as any), ...sxProps, marginTop: "0px" }}
      onChange={(_, item) => {
        fieldOnChange(item);

        if (item) {
          setValue(fieldName, item[valueKey]);
          if (typeof onChange === "function") {
            onChange(item);
          }
        } else {
          setValue(fieldName, null);
        }
      }}
      onBlur={onBlur}
      getOptionLabel={(option) => (option ? option[labelKey] : "")}
      isOptionEqualToValue={(option, currentValue) =>
        option && currentValue
          ? option[valueKey] === currentValue[valueKey]
          : option === currentValue
      }
      renderOption={(props, option, { selected }) => (
        <li
          {...props}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            backgroundColor: selected ? "#cccccc !important" : "#fff !important",
            color: selected ? "#000" : "#797979",
            fontFamily: "Poppins",
            fontWeight: "200",
            fontSize: "16px",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#ccc !important";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = selected
              ? "#cccccc !important"
              : "#fff !important";
          }}
        >
          {getOptionIcon(option) && (
            <Avatar
              variant="rounded"
              src={getOptionIcon(option)}
              sx={{
                width: 24,
                height: 18,
              }}
            />
          )}

          <Typography variant="body2">
            {option[labelKey]}
            {option.phone ? ` (+${option.phone})` : ""}
          </Typography>
        </li>
      )}
      componentsProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 5],
              },
            },
          ],
          sx: {
            "& .MuiAutocomplete-paper": {
              backgroundColor: "#fff",
              color: "#797979",
            },
          },
        },
      }}
      renderInput={(params) =>
        renderInput(params, selectedOption, fieldError?.message, !!fieldError)
      }
    />
  );
};

const CustomAutoCompleteSelectWithImage: React.FC<
  AutoCompleteSelectWithImageProps
> = ({
  name,
  toolTipTitle,
  required,
  fieldName,
  valueKey = "id",
  labelKey = "label",
  options = [],
  iconKey = "icon",
  sxProps,
  objectOnChange,
  validation = {},
  control,
  label = "Country",
  placeholder = "Select Country",
  value,
  onChange,
  error,
  helperText,
  disabled,
  width = "100%",
  countries,
  ...rest
}) => {
  const { setValue } = useFormContext();
  const selectOptions = countries || options;

  const defaultAutocompleteSx: SxProps = {
    width,
    mt: '0px !important',
    "& .MuiAutocomplete-option": {
      "&.Mui-selected": {
        backgroundColor: "#cccccc !important",
      },
    },
    "& .MuiAutocomplete-paper": {
      backgroundColor: "#fff",
      color: "#797979",
    },
  };

  const getOptionIcon = (option?: SelectImageType) => {
    if (!option) return "";

    if (option[iconKey]) return option[iconKey];
    if (option.image) return option.image;
    if (option.code) {
      return `https://flagcdn.com/w40/${option.code.toLowerCase()}.png`;
    }

    return "";
  };

  const renderInput = (
    params: any,
    selectedValue: SelectImageType | null,
    inputErrorMessage?: string,
    hasError: boolean = false,
  ) => (
    <Grid container alignItems="center">
      <Grid item>
        <Grid container alignItems="center">
          <Grid item>
            <Grid container alignItems="center">
              <Grid
                item
                sx={{
                  color: "#A5A5A5",
                  fontSize: "14px",
                  fontFamily: "Poppins",
                  fontWeight: "300",
                  mt: "0px",
                  mb: "4px",
                  ml: "4px",
                  mr: "6px",
                }}
              >
                {label}
              </Grid>
              {toolTipTitle && <CustomTooltip title={toolTipTitle} />}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <TextField
        {...params}
        name={name}
        variant="outlined"
        fullWidth
        placeholder={placeholder}
        InputProps={{
          ...params.InputProps,
          startAdornment:
            selectedValue && getOptionIcon(selectedValue) ? (
              <InputAdornment position="start">
                <Avatar
                  variant="rounded"
                  src={getOptionIcon(selectedValue)}
                  sx={{
                    width: 24,
                    height: 18,
                  }}
                />
              </InputAdornment>
            ) : null,
        }}
        inputProps={{
          ...params.inputProps,
          autoComplete: "new-password",
        }}
        error={hasError}
        helperText={hasError ? inputErrorMessage : helperText}
        required={required}
        disabled={disabled}
        sx={{
          bgcolor: "transparent",
          borderRadius: "100px",
          color: "#797979",
          fontWeight: "300",
          "& .MuiInputLabel-root": {
            color: "#ffffff",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "100px",
            color: "#797979",
            fontSize: "16px",
            fontWeight: "200",
            backgroundColor: "#fff",
            paddingLeft: "12px !important",
            "& fieldset": {
              borderRadius: "100px",
            },
            "&:hover fieldset": {
              borderColor: "#ccc",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#ccc",
            },
            "& input": {
              color: "#000",
            },
            "& .MuiSvgIcon-root": {
              color: "#A5A5A5",
            },
            "& .MuiSelect-select": {
              color: "#000",
            },
          },
          "& .MuiPaper-root": {
            backgroundColor: "#fff !important",
            color: "#000",
            "& .MuiMenuItem-root:hover": {
              backgroundColor: "#fff",
              color: "#000",
            },
          },
        }}
      />
    </Grid>
  );

  const autocomplete = (
    <Autocomplete
      options={selectOptions}
      size="small"
      autoHighlight
      fullWidth
      disabled={disabled}
      value={value || null}
      defaultValue={null}
      sx={{ ...(defaultAutocompleteSx as any), ...sxProps }}
      onChange={(_, item) => {
        if (typeof onChange === "function") {
          onChange(item);
        }

        if (name && control && fieldName && item) {
          setValue(fieldName, item[valueKey]);
        }
      }}
      getOptionLabel={(option) => (option ? option[labelKey] : "")}
      isOptionEqualToValue={(option, currentValue) =>
        option && currentValue
          ? option[valueKey] === currentValue[valueKey]
          : option === currentValue
      }
      renderOption={(props, option, { selected }) => (
        <li
          {...props}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            backgroundColor: selected ? "#cccccc !important" : "#fff !important",
            color: selected ? "#000" : "#797979",
            fontFamily: "Poppins",
            fontWeight: "200",
            fontSize: "16px",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#ccc !important";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = selected
              ? "#cccccc !important"
              : "#fff !important";
          }}
        >
          {getOptionIcon(option) && (
            <Avatar
              variant="rounded"
              src={getOptionIcon(option)}
              sx={{
                width: 24,
                height: 18,
              }}
            />
          )}

          <Typography variant="body2">
            {option[labelKey]}
            {option.phone ? ` (+${option.phone})` : ""}
          </Typography>
        </li>
      )}
      componentsProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 5],
              },
            },
          ],
          sx: {
            "& .MuiAutocomplete-paper": {
              backgroundColor: "#fff",
              color: "#797979",
            },
          },
        },
      }}
      renderInput={(params) =>
        renderInput(
          params,
          value || null,
          typeof helperText === "string" ? helperText : undefined,
          !!error,
        )
      }
    />
  );

  if (name && control && fieldName) {
    return (
      <Controller
        name={name}
        rules={validation}
        control={control}
        render={({
          field: { onBlur, onChange: fieldOnChange, value: fieldValue },
          fieldState: { error: fieldError },
        }) => (
          <ControlledAutocompleteField
            fieldValue={fieldValue}
            fieldOnChange={fieldOnChange}
            onBlur={onBlur}
            fieldError={fieldError}
            selectOptions={selectOptions}
            valueKey={valueKey}
            labelKey={labelKey}
            iconKey={iconKey}
            fieldName={fieldName}
            defaultAutocompleteSx={defaultAutocompleteSx}
            sxProps={sxProps}
            disabled={disabled}
            onChange={onChange}
            getOptionIcon={getOptionIcon}
            renderInput={renderInput}
          />
        )}
      />
    );
  }

  return autocomplete;
};

export default CustomAutoCompleteSelectWithImage;